import { useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { CHAT_MESSAGE_TYPE } from '@medflex/utils/constants';
import getFullNameWithTitle from '@medflex/utils/getFullNameWithTitle';
import isStaffUserId from '@medflex/utils/isStaffUserId';
import isPhysicianUserId from '@medflex/utils/isPhysicianUserId';
import useUserProfile from './useUserProfile';

const getNewestMessageSeenByAll = (participants, participant) => {
  if (!participant) return null;

  const recipients = participants.filter(
    ({ participantId, roles }) =>
      participantId !== participant.participantId && !(roles && roles.includes('organizer')),
  );
  const lastSeens = recipients.map(
    ({ lastSeenMessageTimestamp }) =>
      lastSeenMessageTimestamp && new Date(lastSeenMessageTimestamp),
  );
  return lastSeens.every(l => l) && new Date(Math.min(...lastSeens));
};

const getNewestMessageSeenByPatient = (participants, participant) => {
  if (!participant) return null;

  const recipient = participants.find(
    ({ participantId, physicianUserId }) =>
      participantId !== participant.participantId && !physicianUserId,
  );
  if (!recipient) return null;

  return recipient.lastSeenMessageTimestamp && new Date(recipient.lastSeenMessageTimestamp);
};

const getNewestMessageSeenByPractice = (participants, participant, chatPhysicianUserId) => {
  if (!participant) return null;

  const lastSeenByAtLeastOne = participants.reduce(
    (acc, { participantId, physicianUserId, lastSeenMessageTimestamp }) => {
      if (
        participantId !== participant.participantId &&
        physicianUserId === chatPhysicianUserId &&
        lastSeenMessageTimestamp
      ) {
        acc.push(new Date(lastSeenMessageTimestamp));
      }
      return acc;
    },
    [],
  );

  return new Date(Math.max(...lastSeenByAtLeastOne));
};

const getSeenBy = (senderParticipantId, { participants, messageCreatedAt }) =>
  participants.filter(
    p =>
      senderParticipantId !== p.participantId &&
      p.lastSeenMessageTimestamp &&
      new Date(messageCreatedAt) <= new Date(p.lastSeenMessageTimestamp),
  );

const useSessionChat = ({ id, load, sessionParticipantId, tan }) => {
  const { t } = useTranslation('messenger');
  const dispatch = useDispatch();
  const { userProfile } = useUserProfile();
  const chats = useSelector(state => state.sessionChats);
  const {
    status = {},
    chat = null,
    participant: claimedParticipant,
    errors,
    messages: rawMessages,
  } = chats[id] || {};

  const loadChat = useCallback(() => {
    if (id && !chat) {
      return dispatch.sessionChats.loadChat({ id });
    }
    return null;
  }, [id, chat, dispatch.sessionChats]);

  const loadMessages = useCallback(() => {
    if (id && !rawMessages?.length) {
      return dispatch.sessionChats.loadMessages({ id, participantId: sessionParticipantId, tan });
    }
    return null;
  }, [dispatch.sessionChats, id, rawMessages?.length, sessionParticipantId, tan]);

  const isPracticeToSinglePatientChat =
    chat?.participants.filter(p => !p.physicianUserId).length === 1 &&
    !chat.participants.some(p => p.physicianUserId && p.physicianUserId !== chat?.physicianUserId);

  const send = messageData =>
    dispatch.sessionChats.send({ id, participantId: sessionParticipantId, tan, ...messageData });

  const normalizeSystemMessage = attrs => {
    const {
      payload: { addedParticipant, removedParticipant, actingParticipant },
      ...rest
    } = attrs;
    const wasAdded = Boolean(addedParticipant);
    const wasRemoved = Boolean(removedParticipant);
    const hasLeft =
      wasRemoved &&
      actingParticipant &&
      removedParticipant.participantId === actingParticipant.participantId;

    switch (true) {
      case wasAdded:
        return {
          ...rest,
          participantAdded: getFullNameWithTitle({
            ...addedParticipant,
            id: addedParticipant.userId,
          }),
        };
      case wasRemoved:
      case hasLeft:
        return {
          ...rest,
          participantRemoved: getFullNameWithTitle({
            ...removedParticipant,
            id: removedParticipant.userId,
          }),
          hasLeft,
        };
      default:
        return attrs;
    }
  };

  const messages = useMemo(() => {
    if (chat) {
      const newestSeenByAllDate = getNewestMessageSeenByAll(chat.participants, claimedParticipant);
      const newestSeenByPatientDate = getNewestMessageSeenByPatient(
        chat.participants,
        claimedParticipant,
      );
      const newestSeenByPracticeDate = getNewestMessageSeenByPractice(
        chat.participants,
        claimedParticipant,
        chat.physicianUserId,
      );

      return rawMessages.map(({ attributes, ...meta }) => {
        const sender = chat.participants.find(p => p.participantId === attributes.sender);
        const isSystemMessage = attributes.type === CHAT_MESSAGE_TYPE.SYSTEM;
        const isSender = attributes.sender === claimedParticipant.participantId;
        const isChatOwner = userProfile.id === chat.physicianUserId;

        const participantIdBelongsToPhysicianOrStaff = chat?.participants.reduce(
          (acc, participant) => {
            switch (true) {
              case isChatOwner:
                acc[participant.participantId] =
                  participant.userId &&
                  (isStaffUserId(participant.userId) || isPhysicianUserId(participant.userId)) &&
                  participant.physicianUserId === chat.physicianUserId;
                break;
              case isStaffUserId(userProfile.id):
                acc[participant.participantId] =
                  participant.userId === chat.physicianUserId ||
                  (participant.userId &&
                    isStaffUserId(participant.userId) &&
                    participant.physicianUserId === chat.physicianUserId);
                break;
              default:
                break;
            }
            return acc;
          },
          {},
        );

        const isInSenderPractice =
          participantIdBelongsToPhysicianOrStaff[attributes.sender] && !userProfile.flags.isPatient;

        const getSeenStatus = () => {
          if (isPracticeToSinglePatientChat && isInSenderPractice) {
            return newestSeenByPatientDate && new Date(attributes.time) <= newestSeenByPatientDate;
          }
          if (isSender && isPracticeToSinglePatientChat && !isInSenderPractice) {
            return (
              newestSeenByPracticeDate && new Date(attributes.time) <= newestSeenByPracticeDate
            );
          }
          return (
            isSender && newestSeenByAllDate && new Date(attributes.time) <= newestSeenByAllDate
          );
        };

        return {
          ...meta,
          ...(isSystemMessage ? normalizeSystemMessage(attributes) : attributes),
          ...(attributes.important && { markedImportantAt: new Date().toISOString() }),
          sessionType: chat.type,
          sender: { id: attributes.sender || (isSystemMessage && 'system') },
          isSender,
          isInSenderPractice,
          seenBy:
            isSender && !isPracticeToSinglePatientChat
              ? getSeenBy(sender.participantId, {
                  participants: chat.participants,
                  messageCreatedAt: attributes.time,
                })
              : undefined,
          seen: attributes.time && getSeenStatus(),
          user: {
            fullNameWithTitle: sender
              ? getFullNameWithTitle({ id: sender.userId, ...sender })
              : t('useSessionChat.removedParticipant'),
            jobTitle: sender && (sender.workArea1 || sender.jobTitle),
            avatar: sender && sender.avatar,
            initials: sender ? `${sender.firstName[0]}${sender.lastName[0]}` : 'EN',
            userId: sender?.userId,
            dateOfBirth: sender?.dateOfBirth,
          },
        };
      });
    }

    return [];
  }, [
    chat,
    claimedParticipant,
    rawMessages,
    userProfile.id,
    userProfile.flags.isPatient,
    t,
    isPracticeToSinglePatientChat,
  ]);

  const setMessageImportant = ({ id: messageId, important }) =>
    dispatch.sessionChats.setMessageImportant({
      id,
      messageId,
      important,
    });

  const setLastReadMessage = ({ id: messageId }) =>
    dispatch.sessions.setLastReadMessageId({
      id,
      messageId,
    });

  const deleteMessage = ({ id: messageId }) =>
    dispatch.sessionChats.deleteMessage({
      id,
      messageId,
    });

  const deleteMessages = () =>
    dispatch.sessionChats.deleteMessages({
      id,
    });

  const removeParticipant = ({ id: participantIdToDelete }) =>
    dispatch.sessions.removeParticipant({
      sessionId: id,
      participantId: participantIdToDelete,
    });

  const addParticipant = newParticipant =>
    dispatch.sessions.addParticipant({
      sessionId: id,
      participant: newParticipant,
    });

  const update = attributes =>
    dispatch.sessions.authUpdate({
      sessionId: id,
      ...attributes,
    });

  const remove = () => dispatch.sessions.removeChat({ id });

  const checkChatsStatus = useCallback(async () => {
    await loadChat();
    await loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (load && !chat) {
      checkChatsStatus();
    }
  }, [chat, checkChatsStatus, load]);

  return {
    status,
    send,
    chat,
    errors,
    messages,
    loadMessages,
    setMessageImportant,
    setLastReadMessage,
    deleteMessage,
    deleteMessages,
    removeParticipant,
    addParticipant,
    update,
    remove,
  };
};

export default useSessionChat;
